'use client';
import React, { useState } from 'react';
import { Popover } from 'antd';
import Question from 'Images/svg/question.svg';
import PhoneNumberField from 'Common/components/PhoneNumberField';
import GooglePlacesAutocomplete from 'Common/components/GooglePlacesAutocomplete';
import { AutocompletePrediction } from 'react-places-autocomplete';
import { FieldInputProps } from 'formik';
import { getCurrencyFromCountry } from './functions';

type formikI = {
  values: Record<string, string>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  setFieldValue: (n: string, v: string) => void;
  setFieldTouched: (n: string) => void;
  getFieldProps: (n: string) => FieldInputProps<any>;
}

type props = {
  formik: formikI;
}

const ServiceProviderLocationInput = ({ formik }: props) => {
  const handleAddressSelect = async (place: AutocompletePrediction | undefined) => {
    if (!place) { return; }
    formik.setFieldValue('placeId', place.place_id);

    const country = place.terms[place.terms.length - 1].value;
    formik.setFieldValue('country', country);
    const currency = getCurrencyFromCountry(country);
    if (currency) {
      formik.setFieldValue('currency', currency.value);
    } else {
      formik.setFieldValue('currency', 'cad');
    }
  };

  return (
    <label>
      <span className="flex gap-2">
        Address
        <Popover
          content={(
            <div className="max-w-96 flex flex-col gap-4">
              <p className="font-bold">
                The address you provide is crucial as it determines the customer base that can view and access your services.
              </p>
              <p>
                If you do not have an official business address, you have the flexibility to use your residential address or any other address within your local area.
              </p>
              <p>
                Please note that you have the option to update this address in the future if your circumstances or preferences change.
              </p>
            </div>
          )}>
          <Question />
        </Popover>
      </span>
      <div>
        <GooglePlacesAutocomplete
          placeholder='Address'
          data-testid="accountDetailsStage"
          onSelect={(value) => {
            if (value) {
              handleAddressSelect(value);
              formik.setFieldValue('placeId', value.place_id);
            }
          }}
          onBlur={() => formik.setFieldTouched('placeId')}
        />
        {formik.touched.placeId && formik.errors.placeId && (
          <span className="error">{formik.errors.placeId}</span>
        )}
      </div>
      <small>
        This address identifies which customers can see your business; you may use a retail, home or nearby address if necessary. It is never shown publicly.
      </small>
    </label>
  );
};

type ServiceProviderAccountDetailsStageProps = {
  formik: formikI;
}

const ServiceProviderAccountDetailsStage: React.FC<ServiceProviderAccountDetailsStageProps> = ({ formik }) => {
  const [phoneNumber, setPhoneNumber] = useState('');

  return (
    <div className="flex flex-col justify-center items-start w-full">
      <span className="heading">Profile Information</span>
      <div className="flex flex-col w-full my-4 gap-4">
        <div>
          <label>
            Business Name
            <input
              type="text"
              className="input-lg"
              {...formik.getFieldProps('businessName')}
            />
          </label>
          {formik.touched.businessName && formik.errors.businessName && (
            <div className="error">{formik.errors.businessName}</div>
          )}
        </div>
        <ServiceProviderLocationInput formik={formik} />
        <PhoneNumberField
          value={phoneNumber}
          label="Business Phone #"
          onChange={v => {
            setPhoneNumber(v);
            formik.setFieldValue('phoneNumber', v);
          }}
        />
        {formik.touched.phoneNumber && formik.errors.phoneNumber && (
          <span className="error">
            {formik.errors.phoneNumber}
          </span>
        )}
      </div>
    </div>
  );
};

export default ServiceProviderAccountDetailsStage;
